import { NgTemplateOutlet } from '@angular/common';
import type { TemplateRef } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  output,
  inject,
  input,
} from '@angular/core';

import moment from 'moment-timezone';
import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { LocalStorageService } from '@core/common-legacy';
import type {
  IconName,
  IconSize,
  IconToken,
  RtInfoboxButtonVariant,
} from '@core/user-interface-legacy';
import {
  BadgeComponent,
  ButtonComponent,
  DividerComponent,
  GroupComponent,
  HeadingComponent,
  IconButtonComponent,
  IconComponent,
  MessageComponent,
  RtStatefulComponent,
} from '@core/user-interface-legacy';

type Visibility = 'visible' | 'hidden';

type State = {
  hidden: Visibility;
};

export type InfoboxButton = {
  text: string;
  redirect?: string;
  actionFunction?: Function;
  variant: RtInfoboxButtonVariant;
};

export type InfoboxType = 'success' | 'information' | 'brand' | 'warning' | 'error' | 'transparent';

@Component({
  selector: 'rt-infobox',
  templateUrl: './infobox.component.html',
  styleUrls: ['./infobox.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.hidden]': 'isHidden',
  },
  imports: [
    MessageComponent,
    IconComponent,
    NgTemplateOutlet,
    HeadingComponent,
    BadgeComponent,
    DividerComponent,
    GroupComponent,
    ButtonComponent,
    IconButtonComponent,
  ],
})
export class InfoboxComponent extends RtStatefulComponent<State> {
  private static PREFIX: string = 'infobox__';

  private readonly localStorageService = inject(LocalStorageService);
  protected readonly cdRef: ChangeDetectorRef;

  public readonly storageKey = input<string>(undefined!);
  public readonly isDismissible = input<boolean>(true);
  public readonly type = input<InfoboxType>('information');
  public readonly iconSize = input<IconSize>('medium');
  public readonly iconColor = input<IconToken>(undefined!);
  public readonly closeIconSize = input<IconSize>('small');
  public readonly iconMarginRightSmall = input<boolean>(false);
  public readonly headingBadge = input<string>(undefined!);
  public readonly rounded = input<boolean>(true);
  public readonly radiusSmall = input<boolean>(false);
  public readonly noBorder = input<boolean>(undefined!);
  public readonly borderBottom = input<boolean>(undefined!);
  public readonly messagePaddingSmall = input<boolean>(false);
  public readonly isBadgeBeforeHeading = input<boolean>(false);
  public readonly daysToRenewal = input<number>(undefined!);
  public readonly hasDotDivider = input(true);

  @Input() public iconName: IconName;
  @Input() public heading: string;
  @Input() public subheading: string;
  @Input() public subheadingTpl: TemplateRef<unknown>;
  @Input() public buttons?: InfoboxButton[] = [];
  @Input() public imageTemplate: TemplateRef<any>;

  public dismissClick = output<MouseEvent>();

  public isHidden$: Observable<boolean>;

  constructor() {
    const cdRef = inject(ChangeDetectorRef, { skipSelf: true });

    super(cdRef);
    this.cdRef = cdRef;

    this.createState({
      hidden: 'hidden',
    });
    this.isHidden$ = this.state$.pipe(map(state => state.hidden === 'hidden'));
  }

  public ngOnInit(): void {
    this.setState({ ...this.state, hidden: this.getInfoboxVisibility()! });
  }

  public get isHidden(): boolean {
    return this.state.hidden === 'hidden';
  }

  public get messageVariant(): string {
    return `context-${this.type()}`;
  }

  public closeInfoBox(event: MouseEvent): void {
    this.setState({
      ...this.state,
      ...{
        hidden: 'hidden',
      },
    });
    this.saveInfoboxVisibility();
    this.dismissClick.emit(event);
  }

  public trackBy(index: number, item: InfoboxButton): string {
    return item.text;
  }

  private get componentStorageKey(): string {
    return InfoboxComponent.PREFIX + this.storageKey();
  }

  private saveInfoboxVisibility(): void {
    if (this.storageKey()) {
      const daysToRenewal = this.daysToRenewal();
      this.localStorageService.set(
        this.componentStorageKey,
        !!daysToRenewal
          ? {
              hidden: this.state.hidden,
              renewal: moment().add(daysToRenewal, 'days').valueOf(),
            }
          : {
              hidden: this.state.hidden,
            },
      );
    }
  }

  private getInfoboxVisibility(): Visibility | undefined {
    const boxStateData = this.localStorageService.get(this.componentStorageKey);

    if (!boxStateData) return undefined;

    if (boxStateData.renewal) {
      const now = moment();
      if (now.valueOf() > boxStateData.renewal) {
        this.localStorageService.remove(this.componentStorageKey);
        return undefined;
      }
    }
    return boxStateData.hidden;
  }
}
